import React, { useState, useEffect } from "react";
import { BiCopyAlt } from "react-icons/bi";
import { FaEnvelopeOpenText } from "react-icons/fa";
import { Toggle } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import {
    getSmsHistoryApi,
    getSmsPreferenceApi,
    updateSmsPreferenceApi,
} from "../../../../redux/slice/customers/customers.api";
import Loading from "../../../../components/elements/Loading/Loading";
import { SuccessToast } from "../../../../components/blocks/Alert";

const SmsHistory = ({ customer }: { customer: any }) => {
    const [history, setHistory] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const [smsPref, setSmsPref] = useState<boolean>(false);
    const [pendingPref, setPendingPref] = useState<boolean | null>(null);
    const [prefLoading, setPrefLoading] = useState(false);
    const [showConfirmBox, setShowConfirmBox] = useState(false);

    const getHistory = async () => {
        try {
            setLoading(true);
            const params = {
                phoneNumber: customer.phone_number,
                search: "password",
            };
            const res = await getSmsHistoryApi(params);
            setHistory(res.data);
        } catch (err) {
            console.error("Failed to fetch SMS history", err);
        } finally {
            setLoading(false);
        }
    };

    const getPreference = async () => {
        try {
            setPrefLoading(true);
            if (!customer?.walletRecords?.accountReference) {
                console.warn("No wallet_key found in customer object.");
                return;
            }
            const params = {
                wallet_key: customer?.walletRecords?.accountReference,
            };
            const res = await getSmsPreferenceApi(params);
            setSmsPref(res.wants_sms_alerts);
        } catch (err) {
            console.error("Failed to fetch SMS preference", err);
        } finally {
            setPrefLoading(false);
        }
    };

    const handlePreferenceToggle = (checked: boolean) => {
        setPendingPref(checked);
        setShowConfirmBox(true);
    };

    const confirmPreferenceChange = async () => {
        if (pendingPref === null || !customer?.walletRecords?.accountReference)
            return;

        setPrefLoading(true);
        const payload = {
            wallet_key: customer?.walletRecords?.accountReference,
            wants_sms_alerts: pendingPref,
        };
        try {
            await updateSmsPreferenceApi(payload);
            setSmsPref(pendingPref);
            SuccessToast("SMS preference updated successfully");
        } catch (err) {
            console.error("Failed to update SMS preference", err);
        } finally {
            setPrefLoading(false);
            setShowConfirmBox(false);
            setPendingPref(null);
        }
    };

    const cancelPreferenceChange = () => {
        setPendingPref(null);
        setShowConfirmBox(false);
    };

    useEffect(() => {
        if (!customer) return;
        getPreference();
        getHistory();
    }, [customer]);

    return (
        <div className="container mx-auto space-y-4 h-max bg-gray-50 flex flex-col p-2">
            <div className="flex flex-col gap-1 bg-lola-orange/10 p-1.5 mb-2">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                        <p className="font-semibold text-blue-800">
                            SMS Alerts
                        </p>
                        <span className="text-xs text-gray-500">
                            Modify customer&apos;s SMS Preference
                        </span>
                    </div>
                    <Toggle
                        checked={pendingPref !== null ? pendingPref : smsPref}
                        onChange={handlePreferenceToggle}
                        loading={prefLoading}
                        disabled={
                            !customer?.walletRecords?.accountReference ||
                            prefLoading
                        }
                    />
                </div>

                {/* Slide down confirmation box */}
                {showConfirmBox && (
                    <div className="mt-2 flex gap-2 justify-end transition-all duration-300 ease-in-out">
                        <button
                            type="button"
                            disabled={prefLoading}
                            onClick={confirmPreferenceChange}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-40 text-sm"
                        >
                            Confirm
                        </button>
                        <button
                            type="button"
                            disabled={prefLoading}
                            onClick={cancelPreferenceChange}
                            className="px-3 py-1 bg-gray-400 text-white rounded hover:bg-gray-500 disabled:opacity-40 text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            {loading ? (
                <Loading />
            ) : (
                <div className="flex flex-col gap-4 overflow-y-auto">
                    {history && history.length > 0 ? (
                        history.map((hist, index) => (
                            <li
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                className="flex flex-col gap-1 p-2 rounded-lg border border-blue-200 bg-blue-100"
                            >
                                <p className="font-medium border-b border-black/5">
                                    {hist.body}
                                </p>
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigator.clipboard.writeText(hist.body)
                                    }
                                    className="flex items-center gap-1 ml-auto cursor-pointer p-1 text-xs w-fit bg-blue-300 hover:bg-blue-400 text-blue-900 font-medium rounded border border-inherit"
                                >
                                    <BiCopyAlt className="text-blue-700" /> Copy
                                    to clipboard
                                </button>
                            </li>
                        ))
                    ) : (
                        <section className="flex flex-col items-center bg-blue-50 text-blue-500 border border-blue-100 rounded py-24">
                            <FaEnvelopeOpenText className="w-6 h-6" />
                            <p className="font-semibold">
                                No SMS Password/Pin Yet...
                            </p>
                        </section>
                    )}
                </div>
            )}
        </div>
    );
};

export default SmsHistory;
